import React, { useCallback } from "react";

import { faAngleLeft } from "@fortawesome/pro-solid-svg-icons/faAngleLeft";
import { faAngleRight } from "@fortawesome/pro-solid-svg-icons/faAngleRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { Activity } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import useCalendarView from "@app/component/view/CalendarView/utils/useCalendarView";
import CalendarViewMode from "@app/enum/CalendarViewMode";
import {
  getNextTimeframeStart,
  getPreviousTimeframeStart,
} from "@app/scene/activity/utils/navigation";
import { AnalyticsService } from "@app/util/analytic/AnalyticsService";

import styles from "./NavigationButtons.module.scss";

const messages = defineMessages({
  next: {
    id: "activities.calendarView.calendar.toolbar.navigation.next",
    defaultMessage: "Next{viewMode, select, day { Day} week { Week} month { Month} other {}}",
    description: "Tooltip for the prev button in calendar toolbar in Activities Calendar",
  },
  previous: {
    id: "activities.calendarView.calendar.toolbar.navigation.prev",
    defaultMessage: "Previous{viewMode, select, day { Day} week { Week} month { Month} other {}}",
    description: "Tooltip for the prev button in calendar toolbar in Activities Calendar",
  },
});

interface Props {
  analyticIssuer?: AnalyticsService;
  className?: string;
  forceViewMode?: CalendarViewMode;
  timeframeStart: Date;
}

const NavigationButtons: React.FC<Props> = ({
  analyticIssuer,
  className,
  forceViewMode,
  timeframeStart,
}) => {
  const intl = useIntl();
  const { updateFilterModel, viewState } = useCalendarView<Activity>();

  const handleGoBack = useCallback(
    () => {
      updateFilterModel({
        startAt: {
          operator: FilterOperator.ON,
          value: getPreviousTimeframeStart(timeframeStart, forceViewMode ?? viewState.viewMode),
        },
      });
      analyticIssuer?.clicked([`Previous ${viewState.viewMode}`]);
    },
    // ignoring deps because we know that startAtDate.getTime() is a valid replacement for date
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analyticIssuer, updateFilterModel, forceViewMode, viewState.viewMode, timeframeStart.getTime()]
  );

  const handleGoForward = useCallback(
    () => {
      updateFilterModel({
        startAt: {
          operator: FilterOperator.ON,
          value: getNextTimeframeStart(timeframeStart, forceViewMode ?? viewState.viewMode),
        },
      });
      analyticIssuer?.clicked([`Next ${viewState.viewMode}`]);
    },
    // ignoring deps because we know that startAtDate.getTime() is a valid replacement for date
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [analyticIssuer, updateFilterModel, forceViewMode, viewState.viewMode, timeframeStart.getTime()]
  );

  return (
    <div className={cn(styles.container, className)}>
      <Tooltip
        title={intl.formatMessage(messages.previous, {
          viewMode: forceViewMode ?? viewState.viewMode,
        })}
      >
        <Button
          className={styles.navButtonLeft}
          icon={<FontAwesomeIcon icon={faAngleLeft} />}
          iconOnly
          onClick={handleGoBack}
          type="text"
        />
      </Tooltip>
      <Tooltip
        title={intl.formatMessage(messages.next, { viewMode: forceViewMode ?? viewState.viewMode })}
      >
        <Button
          className={styles.navButtonRight}
          icon={<FontAwesomeIcon icon={faAngleRight} />}
          iconOnly
          onClick={handleGoForward}
          type="text"
        />
      </Tooltip>
    </div>
  );
};

export default NavigationButtons;
